import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "react-router";
import { ApiError } from "../../api/client";
import { sessionApi, type CustomerSession } from "../../api/account";

export const SESSION_KEY = ["session"] as const;

export function useCustomerSession() {
  return useQuery({ queryKey: SESSION_KEY, queryFn: sessionApi.get, staleTime: 60_000 });
}

/**
 * Records a sign-in or sign-out. Everything else is refetched: prices are for whoever is
 * looking (design doc §2.7), and a signed-out browser must not keep the account's data.
 */
export function useSetSession() {
  const queryClient = useQueryClient();
  return (session: CustomerSession) => {
    queryClient.setQueryData(SESSION_KEY, session);
    if (!session.user) queryClient.removeQueries({ queryKey: ["account"] });
    queryClient.invalidateQueries({ predicate: (query) => query.queryKey[0] !== SESSION_KEY[0] });
  };
}

/** Where to go after signing in: the page that sent the customer here, or the storefront. */
export function useReturnTo(): string {
  const state = useLocation().state as { from?: string } | null;
  return state?.from ?? "/";
}

/** An API error's message, or a generic one for a network failure. */
export function errorMessage(error: Error | null): string | undefined {
  if (!error) return undefined;
  return error instanceof ApiError ? error.message : "Something went wrong. Check your connection and try again.";
}

/** The first message the API gave for one field. */
export function fieldError(error: Error | null, field: string): string | undefined {
  return error instanceof ApiError ? error.details[field]?.[0] : undefined;
}
